import {
  RetryConfig,
  DEFAULT_RETRY_CONFIG,
  ErrorContext,
  isRetryableError,
  isNonRetryableError,
  logError,
  calculateRetryDelay,
  sleep,
  ApiError
} from './errorHandlingUtils';

/**
 * Centralized retry utilities for consistent retry behavior across services
 * Integrates with error handling utilities for comprehensive error management
 */

/**
 * Enhanced retry wrapper with comprehensive error handling and AbortController support
 * @template T - The return type of the operation
 * @param operation - The async operation to retry
 * @param context - Context information for logging and error handling
 * @param config - Retry configuration (optional, uses defaults)
 * @param abortSignal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to the operation result
 * @throws ApiError when all retry attempts fail or on non-retryable errors
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  context: ErrorContext,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  abortSignal?: AbortSignal
): Promise<T> {
  let lastError: any;

  for (let attempt = 1; attempt <= config.maxRetries; attempt++) {
    // Check if operation was aborted
    if (abortSignal?.aborted) {
      const abortError = new ApiError(
        `Operation aborted: ${context.operation}`,
        context.service,
        {
          url: context.url,
          method: context.method,
          isRetryable: false
        }
      );
      throw abortError;
    }

    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Check if error is non-retryable
      if (isNonRetryableError(error)) {
        logError(error, { ...context, attempt });
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const status = (error as any)?.response?.status;
        throw new ApiError(
          `Non-retryable error in ${context.operation}: ${errorMessage}`,
          context.service,
          {
            originalError: error instanceof Error ? error : undefined,
            status,
            url: context.url,
            method: context.method,
            isRetryable: false
          }
        );
      }

      // Don't retry on the last attempt
      if (attempt === config.maxRetries) {
        break;
      }

      // Check if error is retryable
      if (!isRetryableError(error)) {
        // Non-retryable error, throw immediately
        logError(error, { ...context, attempt });
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const status = (error as any)?.response?.status;
        throw new ApiError(
          `Non-retryable error in ${context.operation}: ${errorMessage}`,
          context.service,
          {
            originalError: error instanceof Error ? error : undefined,
            status,
            url: context.url,
            method: context.method,
            isRetryable: false
          }
        );
      }

      // Calculate delay and wait
      const delay = calculateRetryDelay(config, attempt, error);
      logError(error, { ...context, attempt });

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn(
        `${context.service}: ${context.operation} failed (attempt ${attempt}/${config.maxRetries}), ` +
        `retrying in ${Math.round(delay)}ms: ${errorMessage}`
      );

      await sleep(delay);
    }
  }

  // All retry attempts failed
  const finalError = new ApiError(
    `${context.operation} failed after ${config.maxRetries} attempts`,
    context.service,
    {
      originalError: lastError,
      status: lastError?.response?.status,
      url: context.url,
      method: context.method,
      isRetryable: false
    }
  );

  logError(lastError, { ...context, attempt: config.maxRetries });
  throw finalError;
}

/**
 * Creates a retry-enabled operation wrapper for a specific service
 * @param service - The service name for error context
 * @param config - Optional retry configuration
 * @returns A function that wraps operations with retry logic
 */
export function createRetryWrapper(service: string, config: RetryConfig = DEFAULT_RETRY_CONFIG) {
  return function <T>(
    operation: () => Promise<T>,
    operationName: string,
    url?: string,
    method?: string,
    abortSignal?: AbortSignal
  ): Promise<T> {
    return withRetry(
      operation,
      { operation: operationName, service, url, method },
      config,
      abortSignal
    );
  };
}

/**
 * Batch retry utility for multiple operations with concurrency control
 * @template T - The return type of each operation
 * @param operations - Array of operations to execute with retry
 * @param context - Context information for the batch operation
 * @param config - Retry configuration for each operation
 * @param concurrency - Maximum number of concurrent operations (default: 3)
 * @param abortSignal - Optional AbortSignal for batch cancellation
 * @returns Promise resolving to array of operation results
 */
export async function withBatchRetry<T>(
  operations: (() => Promise<T>)[],
  context: ErrorContext,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  concurrency: number = 3,
  abortSignal?: AbortSignal
): Promise<T[]> {
  const results: T[] = [];
  const errors: Error[] = [];

  // Process operations in batches with concurrency control
  for (let i = 0; i < operations.length; i += concurrency) {
    if (abortSignal?.aborted) {
      throw new ApiError(
        `Batch operation aborted: ${context.operation}`,
        context.service,
        { isRetryable: false }
      );
    }

    const batch = operations.slice(i, i + concurrency);
    const batchPromises = batch.map((operation, index) =>
      withRetry(
        operation,
        {
          ...context,
          operation: `${context.operation} (batch item ${i + index + 1})`
        },
        config,
        abortSignal
      ).catch(error => {
        errors.push(error);
        return null as T; // Return null for failed operations
      })
    );

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.filter(result => result !== null));
  }

  // If any operations failed and we have no successful results, throw the first error
  if (errors.length > 0 && results.length === 0) {
    throw errors[0];
  }

  // Log warnings for partial failures
  if (errors.length > 0) {
    console.warn(
      `${context.service}: ${context.operation} completed with ${errors.length} failures out of ${operations.length} operations`
    );
  }

  return results;
}
